import { Icon } from '@iconify/react';
// mdi:chart-bell-curve
import chartBellCurve from '@iconify/icons-mdi/chart-bell-curve';
// mdi:table-of-contents
import tableOfContents from '@iconify/icons-mdi/table-of-contents';

import PropTypes from 'prop-types';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { styled } from '@mui/material/styles';
import {
  Grid,
  Card,
  Stack,
  useMediaQuery,
  Typography,
  Paper,
  BottomNavigationAction,
  Box,
  BottomNavigation,
  Divider
} from '@mui/material';

import {
  VolShareGraphe,
  VolShareSideBarSettings,
  VolShareSideBarInfo,
  VolShareReplayVol
} from '../components/volShare';
import { MHidden } from '../components/@material-extend';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';

import { useEffectAsync } from '../reactHelper';
import { getItem } from '../fetch';
import { Add3DElement, Map, AddSatelliteView, ReplayPathMap3dShare } from '../map';
import { useTranslation } from '../common/LocalizationProvider';
import { FlightReviewIndicators } from '../components/flightReview';

// ----------------------------------------------------------------------
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

// ----------------------------------------------------------------------
const RootStyle = styled('div')(() => ({
  flexGrow: 1
}));

const MapStyle = styled(Card)(({ theme }) => ({
  height: window.innerHeight / 1.8,
  width: '100%',
  position: 'relative',
  [theme.breakpoints.between('xs', 'sm')]: {
    height: window.innerHeight / 2.0
  }
}));

const SideBar = styled(Paper)(({ theme }) => ({
  height: window.innerHeight / 1.15,
  overflow: 'auto',
  [theme.breakpoints.between('xs', 'md')]: {
    width: window.innerWidth,
    height: window.innerHeight / 2.2
  }
}));

export default function VolShare() {
  const t = useTranslation();
  const location = useLocation();
  const Up = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  const [valueTabs, setValueTabs] = useState(0);
  const handleChangeTabs = (event, newValue) => {
    setValueTabs(newValue);
  };

  const [state, setState] = useState({
    info: null,
    positions: null,
    currentPosition: 0,
    playVol: false,
    speedReplay: 1,
    uniteVitesse: 'km/h',
    uniteAltitude: 'ft',
    uniteDistance: 'km'
  });

  const [indexDisplay, setIndexDisplay] = useState(0);

  useEffectAsync(async () => {
    const response = await getItem(`share/vol?token=${location.search.slice(1)}`);
    if (response.ok) {
      const result = await response.json();
      setState({ ...state, info: await result.info, positions: await result.position });
    }
  }, []);

  return (
    <Page title={t('FS_Title')} openProgress={!(state.info !== null && state.positions !== null)}>
      <RootStyle sx={Up ? { mt: -3, mb: -10, mr: 0, ml: 0 } : { mt: -3, mb: -10 }}>
        <Scrollbar
          sx={{
            height: '100%'
          }}
        >
          {state.info !== null && state.positions !== null ? (
            <Stack
              spacing={0}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid container spacing={0}>
                <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                  <Stack spacing={2} direction="column">
                    <MapStyle>
                      <Map>
                        <AddSatelliteView />
                        <Add3DElement />
                        <ReplayPathMap3dShare
                          state={state}
                          setState={setState}
                          setIndexDisplay={setIndexDisplay}
                        />
                      </Map>
                    </MapStyle>
                    <MHidden width="mdDown">
                      <VolShareGraphe
                        state={state}
                        setState={setState}
                        indexDisplay={indexDisplay}
                      />
                    </MHidden>
                    <MHidden width="mdUp">
                      <Box>
                        <BottomNavigation showLabels value={valueTabs} onChange={handleChangeTabs}>
                          <BottomNavigationAction
                            icon={<Icon icon={tableOfContents} width={24} height={24} />}
                          />
                          <BottomNavigationAction
                            icon={<Icon icon={chartBellCurve} width={24} height={24} />}
                          />
                        </BottomNavigation>
                      </Box>

                      <Divider />
                      <SideBar>
                        <TabPanel value={valueTabs} index={0}>
                          <Box sx={{ mt: 3 }}>
                            <Stack spacing={5} direction="column" alignItems="center">
                              <VolShareReplayVol state={state} setState={setState} />
                              {state.playVol ? (
                                <FlightReviewIndicators
                                  positions={state.positions}
                                  indexDisplay={indexDisplay}
                                />
                              ) : (
                                <>
                                  <VolShareSideBarInfo state={state} setState={setState} />
                                  <VolShareSideBarSettings state={state} setState={setState} />
                                </>
                              )}
                            </Stack>
                          </Box>
                        </TabPanel>
                        <TabPanel value={valueTabs} index={1}>
                          <VolShareGraphe
                            state={state}
                            setState={setState}
                            indexDisplay={indexDisplay}
                          />
                        </TabPanel>
                      </SideBar>
                    </MHidden>
                  </Stack>
                </Grid>
                <MHidden width="mdDown">
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <SideBar>
                      <Box sx={{ mt: 3 }}>
                        <Stack spacing={5} direction="column" alignItems="center">
                          <VolShareReplayVol state={state} setState={setState} />

                          {state.playVol ? (
                            <FlightReviewIndicators
                              positions={state.positions}
                              indexDisplay={indexDisplay}
                            />
                          ) : (
                            <>
                              <VolShareSideBarInfo state={state} setState={setState} />
                              <VolShareSideBarSettings state={state} setState={setState} />
                            </>
                          )}
                        </Stack>
                      </Box>
                    </SideBar>
                  </Grid>
                </MHidden>
              </Grid>
            </Stack>
          ) : (
            <Typography>{t('FR_Wait')}</Typography>
          )}
        </Scrollbar>
      </RootStyle>
    </Page>
  );
}
